import React, { useEffect, useRef, useState } from "react";
import { Search, Calendar, Filter, ChevronRight, Eye, X } from "lucide-react";
import { formatDate } from "../../utils/utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Import datepicker CSS
import { useNavigate } from "react-router-dom";
import { MappingStatus, MappingStyles } from "../../utils/utils";

const ActiveApplications = ({ activeApplications, handleViewApplication }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]); // Use array for date range
  const [pickerMode, setPickerMode] = useState("date"); // "date" | "month" | "year"
  const calendarRef = useRef(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Status options
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "in-review", label: "In Review" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "new-leads", label: "New Leads" },
  ];

  const navigate = useNavigate();

  const [startDate, endDate] = dateRange; // Destructure for easier access

  // Helper function to get status safely
  const getStatus = (app) => {
    if (app.status) return app.status;
    return "Unknown";
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // Filter by status
  const filteredByStatus = (app) => {
    if (selectedStatus === "all") return true;
    return getStatus(app).toLowerCase() === selectedStatus.toLowerCase();
  };

  // Add null checks and handle different data structures
  // Filter by search term, status, and date range
  const filteredApplicants = activeApplications.filter((app) => {
    // Check if app exists
    if (!app) return false;

    // Apply date range filter
    let matchesDate = true;
    if (startDate && endDate) {
      const appDate = new Date(app.createdAt || app.date); // Use createdAt or date for filtering
      matchesDate =
        !isNaN(appDate.getTime()) && appDate >= startDate && appDate <= endDate;
    }
    // Apply status filter
    if (!filteredByStatus(app)) return false;

    // Check for user property first (from backend data structure)
    if (app.user && app.user.name) {
      return app.user.name.toLowerCase().includes(searchTerm.toLowerCase());
    }

    // Fall back to direct name property if it exists
    if (app.name) {
      return app.name.toLowerCase().includes(searchTerm.toLowerCase());
    }

    // If neither exists or date/status doesn't match, don't include
    return false;
  });

  const handleCheckboxChange = (index) => {
    if (selectedApplicants.includes(index)) {
      setSelectedApplicants(selectedApplicants.filter((i) => i !== index));
    } else {
      setSelectedApplicants([...selectedApplicants, index]);
    }
  };

  const handleSelectAll = () => {
    setSelectedAll(!selectedAll);
    if (!selectedAll) {
      setSelectedApplicants(filteredApplicants.map((_, idx) => idx));
    } else {
      setSelectedApplicants([]);
    }
  };

  // Helper function to get applicant name safely
  const getApplicantName = (app) => {
    if (app.user && app.user.name) return app.user.name;
    if (app.name) return app.name;
    return "Unknown";
  };

  // Helper function to get loan amount safely
  const getLoanAmount = (app) => {
    if (app.principle) return app.principle;
    return "N/A";
  };

  // Helper function to get loan type safely
  const getLoanType = (app) => {
    if (app.loanType) return app.loanType;
    return "N/A";
  };

  // Helper function to get verification status safely
  const getVerification = (app) => {
    if (app.verification) return app.verification;

    // Try to determine from documents if available
    if (app.documents && app.documents.length > 0) {
      const allVerified =
        app.status === "in-review" || app.status === "processing";

      return allVerified ? "Verified" : "Pending";
    }

    return "Pending";
  };

  const handleStatusClick = (app) => {
    if (app.status === "rejected") {
      setSelectedReason(app.rejection_reason || app.rejectionReason || "No rejection reason provided.");
      setShowReasonModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowReasonModal(false);
    setSelectedReason("");
  };

  return (
    <div className="bg-transparent rounded-lg p-4 mt-6 col-span-1 lg:col-span-2">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-white">Active Applications</h3>
        <button
          className="flex items-center text-yellow-500 text-sm"
          onClick={() => navigate("/staff/customers")}
        >
          Go to All Loan Applications <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative  ">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Applicant Name"
            className="bg-transparent border border-[#333333] rounded-lg py-2 pl-10 pr-4 w-full text-sm text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
            <button
              className="flex items-center px-3 py-2 focus:outline-none gap-2"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <svg
                width="14"
                height="16"
                viewBox="0 0 14 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                  fill="#FFCA20"
                />
              </svg>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : getPickerLabel()}
            </button>

            {isDatePickerOpen && (
              <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
                {/* Picker Mode Switch */}
                <div className="flex gap-2 mb-2">
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "date"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("date")}
                  >
                    Date
                  </button>
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "month"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("month")}
                  >
                    Month
                  </button>
                  <button
                    className={`px-3 py-1 rounded ${
                      pickerMode === "year"
                        ? "bg-yellow-500 text-black"
                        : "bg-[#333] text-white"
                    }`}
                    onClick={() => setPickerMode("year")}
                  >
                    Year
                  </button>
                </div>

                {/* Calendar */}
                <DatePicker
                  selected={startDate}
                  onChange={(update) => {
                    if (pickerMode === "date") {
                      setDateRange(update);
                      if (update[0] && update[1]) setIsDatePickerOpen(false);
                    } else {
                      setDateRange([update, update]);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange={pickerMode === "date"}
                  showMonthYearPicker={pickerMode === "month"}
                  showYearPicker={pickerMode === "year"}
                  inline
                />
              </div>
            )}
          </div>

          <div className="relative border border-[#333333] rounded-lg">
            <button
              className="bg-transparent border border-[#333333] rounded-lg px-4 py-2 text-sm flex items-center gap-2 text-white min-w-32"
              onClick={() => setShowStatusDropdown((prev) => !prev)}
            >
              <Filter className="w-4 h-4" />
              {statusOptions.find((opt) => opt.value === selectedStatus)
                ?.label || "All Statuses"}
            </button>
            {showStatusDropdown && (
              <div className="absolute top-full mt-2 right-0 z-10 bg-[#1e1e1e] border border-[#333333] rounded-lg shadow-lg min-w-40">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    className="w-full text-left px-4 py-2 text-sm text-white hover:bg-[#333333] first:rounded-t-lg last:rounded-b-lg"
                    onClick={() => {
                      setSelectedStatus(option.value);
                      setShowStatusDropdown(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead className="bg-[#131313] text-[#F3F3F3]">
            <tr>
              <th className="px-4 py-3 text-left w-10 rounded-tl-lg">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                Application ID
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                Applicant Name
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                Loan Amount
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                Documents
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-x border-[#333333] rounded-tr-lg">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#131313] text-white">
            {filteredApplicants.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-400">
                  No rows found
                </td>
              </tr>
            ) : (
              filteredApplicants.map((app, index) => {
                const isLast = index === filteredApplicants.length - 1;
                return (
                  <tr
                    key={index}
                    className={`border-b border-[#333333] ${
                      isLast ? "rounded-b-lg" : ""
                    } hover:bg-gray-800`}
                  >
                    <td
                      className={`px-4 py-3 ${isLast ? "rounded-bl-lg" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✔'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                        checked={selectedApplicants.includes(index)}
                        onChange={() => handleCheckboxChange(index)}
                      />
                    </td>
                    <td className="px-4 py-3 border-x border-[#333333]">
                      <div>{app._id}</div>
                      <div className="text-xs text-gray-400">
                        {formatDate(app.createdAt || app.date)}
                      </div>
                    </td>
                    <td className="px-4 py-3 border-x border-[#333333]">
                      {getApplicantName(app)}
                    </td>
                    <td className="px-4 py-3 border-x border-[#333333] text-center">
                      <div>{getLoanAmount(app)}</div>
                      <div className="text-xs text-gray-400">
                        {getLoanType(app)}
                      </div>
                    </td>
                    <td className="px-2 md:px-4 py-3 border-x border-[#333333] text-center">
                      <span
                        className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm whitespace-nowrap ${MappingStyles(
                          app.status
                        )} ${app.status === "rejected" ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                        onClick={() => handleStatusClick(app)}
                        title={app.status === "rejected" ? "Click to view rejection reason" : ""}
                      >
                        {MappingStatus(app.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 border-x border-[#333333] text-center">
                      <div
                        className={`px-3 py-1 rounded text-xs inline-block ${
                          getVerification(app).includes("Verified")
                            ? "bg-green-700 bg-opacity-30 text-green-400 rounded-full"
                            : "bg-yellow-700 bg-opacity-30 text-[#FFCA20] rounded-full"
                        }`}
                      >
                        {getVerification(app)}
                      </div>
                    </td>
                    <td
                      className={`px-4 py-3 text-center border-x border-[#333333] ${
                        isLast ? "rounded-br-lg" : ""
                      }`}
                    >
                      <button
                        className="text-[#FFCA20] hover:text-[#FFCA20]"
                        onClick={() => handleViewApplication(app._id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Rejection Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw] border border-[#333333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Rejection Reason</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-white text-sm">
                {selectedReason || "No rejection reason provided."}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-[#FFCA20] text-black rounded-md hover:bg-yellow-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveApplications;
